import { useState } from "react";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import { api, useAuth, formatApiError } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function Login() {
  const { login } = useAuth();
  const { branding, imageUrl } = useBranding();
  const site = branding?.site || {};
  const loginCfg = branding?.login || {};
  const loginImg = imageUrl("login");
  const logoSrc = imageUrl("logo") || "/logo.png";
  const titreSite = [site.titre_l1, site.titre_l2].filter(Boolean).join(" ") || "Suivi des Certifications / Formations";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials");
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);
  const [info, setInfo] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.otp_required) {
        setStep("otp");
        setInfo(`Un code à 6 chiffres vient d'être envoyé à ${email}`);
        return;
      }
      window.location.href = "/";
    } catch (err) {
      if (!err.response) {
        try {
          await new Promise((r) => setTimeout(r, 1200));
          const res = await login(email, password);
          if (res?.otp_required) {
            setStep("otp");
            setInfo(`Un code à 6 chiffres vient d'être envoyé à ${email}`);
            return;
          }
          window.location.href = "/";
          return;
        } catch (err2) {
          setError(!err2.response
            ? "Serveur injoignable — patientez quelques secondes et réessayez."
            : formatApiError(err2, "Connexion impossible"));
        }
      } else if (err.response.status === 401) {
        setError("Email ou mot de passe incorrect.");
      } else if (err.response.status === 429) {
        setError(formatApiError(err, "Trop de tentatives — réessayez dans 15 minutes."));
      } else if (err.response.status >= 500) {
        setError("Erreur serveur temporaire — réessayez dans quelques secondes.");
      } else {
        setError(formatApiError(err, "Connexion impossible"));
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/verify-otp", { email, code, trust_device: trustDevice });
      window.location.href = "/";
    } catch (err) {
      setError(formatApiError(err, "Vérification impossible"));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { email });
      setInfo("Un nouveau code vient d'être envoyé.");
    } catch (err) {
      setError(formatApiError(err, "Renvoi impossible"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      {loginCfg.panneau_gauche !== false && (
      <div className="hidden lg:block relative">
        <img
          src={loginImg || "https://images.unsplash.com/photo-1564144573017-8dc932e0039e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"}
          alt="Illustration de la page de connexion"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/60 to-[#0B0F17]/30" />
        <div className="absolute bottom-0 p-10">
          <p className="text-xs font-mono uppercase tracking-widest text-accent mb-3">{titreSite}</p>
          <h2 className="text-3xl font-extrabold leading-tight" style={{ fontFamily: "Outfit" }}>
            {loginCfg.titre || <><span className="text-emerald-500">SST</span> · <span className="text-red-500">SSIAP</span><br /><span className="text-orange-500">Cartes Pro</span> · <span className="text-sky-500 text-2xl">Formations incendie</span></>}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-sm">
            {loginCfg.description || "Suivi des certifications, planification des sessions et alertes d'expiration par email — tout le magasin, au même endroit."}
          </p>
        </div>
      </div>
      )}

      <div className="flex items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(20,184,166,0.07),transparent_55%)]" />
        <div className="w-full max-w-sm relative">
          <div className="flex flex-col items-center text-center mb-8">
            <img src={logoSrc} alt={titreSite}
              className="h-24 w-24 rounded-3xl object-cover shadow-xl ring-1 ring-border mb-5" data-testid="login-logo" />
            <h1 className="text-2xl font-extrabold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>
              {site.titre_l1 || "Suivi des Certifications"} <span className="text-accent">{site.titre_l2 || "/ Formations"}</span>
            </h1>
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1.5">{site.signature || "by Gerlier Sean"}</p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-xl">
            <p className="text-sm font-semibold mb-5 flex items-center gap-2">
              <Lock className="h-4 w-4 text-accent" /> {loginCfg.texte_carte || "Espace sécurisé — accès restreint"}
            </p>
            {step === "credentials" ? (
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required autoComplete="email" placeholder="vous@exemple.fr"
                  data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-background h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input id="password" type="password" required autoComplete="current-password" placeholder="••••••••"
                  data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-background h-11" />
              </div>
              {error && (
                <p className="text-sm text-red-400 bg-red-950/50 border border-red-800 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>
              )}
              <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-button">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connexion...</> : <><ShieldCheck className="h-4 w-4 mr-2" /> Se connecter</>}
              </Button>
            </form>
            ) : (
            <form onSubmit={verify} className="space-y-5" data-testid="otp-form">
              <p className="text-xs text-muted-foreground bg-secondary/50 border border-border rounded-lg px-3 py-2" data-testid="otp-info">{info}</p>
              <div className="space-y-2">
                <Label htmlFor="otp">Code de vérification</Label>
                <Input id="otp" type="text" inputMode="numeric" autoComplete="one-time-code" required maxLength={6}
                  placeholder="••••••" data-testid="login-otp-input" value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="bg-background h-12 text-center text-2xl font-mono tracking-[0.5em]" />
                <p className="text-[11px] text-muted-foreground">Valable 10 minutes. Vérifiez vos spams si besoin.</p>
              </div>
              <label className="flex items-center gap-2.5 text-xs text-muted-foreground cursor-pointer select-none">
                <Checkbox checked={trustDevice} onCheckedChange={(v) => setTrustDevice(!!v)} data-testid="otp-trust-device" />
                Se souvenir de cet appareil (30 jours) — le code ne sera plus demandé ici
              </label>
              {error && (
                <p className="text-sm text-red-400 bg-red-950/50 border border-red-800 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>
              )}
              <Button type="submit" className="w-full h-11" disabled={loading || code.length !== 6} data-testid="login-otp-submit">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Vérification...</> : <><ShieldCheck className="h-4 w-4 mr-2" /> Valider le code</>}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => { setStep("credentials"); setCode(""); setError(""); setInfo(""); }}
                  className="text-muted-foreground hover:text-foreground transition-colors" data-testid="otp-back-button">
                  ← Retour
                </button>
                <button type="button" onClick={resend} disabled={resending}
                  className="text-accent hover:underline underline-offset-2 disabled:opacity-50" data-testid="otp-resend-button">
                  {resending ? "Envoi..." : "Renvoyer le code"}
                </button>
              </div>
            </form>
            )}
          </div>
          <p className="text-center text-[11px] font-mono uppercase tracking-wider text-muted-foreground mt-6">
            SST · SSIAP · Cartes Pro · Incendie
          </p>
        </div>
      </div>
    </div>
  );
}
